import { Formik, FormikValues } from "formik";
import _ from "lodash";
import { createContext, useContext, useMemo } from "react";
import { ObjectSchema } from "yup";

// https://github.com/jaredpalmer/formik/issues/1241

const FormikSchemaContext = createContext<{ schema: ObjectSchema<any> | undefined, isFieldRequired: (fieldName: string) => boolean }>(undefined);

export function FormikWithSchema<T extends FormikValues = FormikValues>(
    props: Parameters<typeof Formik<T>>[0],
) {
    const isFieldRequired = useMemo(() => {
        return (fieldName: string) => {
            const fieldSchema = getFieldSchema(fieldName, props.validationSchema);
            return fieldSchema?.spec?.presence === 'required';
        };
    }, [props.validationSchema]);
    return (
        <FormikSchemaContext.Provider value={{ schema: props.validationSchema, isFieldRequired }} >
            <Formik {...props} />
        </FormikSchemaContext.Provider>
    );
}

// Custom hook to access the form schema from context
// Needed because useFormikContext does not expose validationSchema despite it being part of FormikProps
export const useFormikSchemaContext = () => useContext(FormikSchemaContext);

function fieldNameToSchemaAccessor(name: string): string {
    // i.e. "address.0.street"  ->  "fields.address.innerType.fields.street"
    const parts = name.split(".");
    for (let i = 0; i < parts.length; i++) {
        if (!isNaN(Number(parts[i]))) {
            parts[i] = "innerType";
        }
    }
    return 'fields.' + parts.join(".fields.");
}

function getFieldSchema(name: string, schema: ObjectSchema<any>) {
    // const schemaDescription = schema.describe(); // describe has some test which should be executed to determine if something is required
    const schemaDescription = schema;
    const accessor = fieldNameToSchemaAccessor(name);
    // console.log('getFieldSchema: name=', name, ' accessor=', accessor, ' schemaDescription=', schemaDescription);
    return _.get(schemaDescription, accessor);
}